import { css } from '@emotion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Fab } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Ref, Suspense, useLayoutEffect } from 'react';
import { Panel, PanelImperativeHandle, Separator, usePanelRef } from 'react-resizable-panels';
import { Breakpoint } from '../../constants/styles';
import { isNavigationOpenAtom } from '../Layout/state/NavigationState';
import { LoadingBarWhileMounted } from '../LoadingBar/LoadingBarWhileMounted';
import { LoadingId } from '../../types';
import { useChatBotPanelStateSync } from './chatbot-panel-hooks';
import { ShowChatBotAtom } from './chatbot-state';
import { lazyShell, Preload } from '../../util/lazyPage';

export { ShowChatBotAtom };

// The ChatBot (and the editor it embeds) stays out of the boot bundle; the warm-up fetches it early
// and the panel's first open loads it otherwise.
const ChatBot = lazyShell(() => import('../../pages/ChatBot/ChatBot'), 'default');
export const preloadChatBot: Preload = () => ChatBot.preload();

export const ChatBotDrawerDefaultWidth = 500;
const ChatBotDrawerStyles = css`
  border-left: var(--mui-palette-primary-light) 1px solid;
  width: 100%;
  position: relative;
  min-height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  background: var(--mui-palette-background-default);
`;

export function ChatBotButton() {
  const [showChatBot, setShowChatBot] = useAtom(ShowChatBotAtom);

  if (showChatBot) {
    // The panel's own header close button takes over while open; an empty cell
    // keeps the app-header grid columns stable (same as the audit-view case).
    return <div />;
  }

  return (
    <Fab
      aria-label='open foresight assistant'
      color='primary'
      size='small'
      title='foresight assistant'
      onClick={() => setShowChatBot(true)}
      sx={{
        zIndex: 0,
        justifySelf: 'end',
      }}
    >
      <AutoAwesomeIcon />
    </Fab>
  );
}

export function ChatBotDrawer() {
  const showChatBot = useAtomValue(ShowChatBotAtom);
  if (!showChatBot) {
    return null;
  }
  return (
    <Box css={ChatBotDrawerStyles}>
      <Suspense fallback={<LoadingBarWhileMounted id={LoadingId.loadChatBot} />}>
        <ChatBot />
      </Suspense>
    </Box>
  );
}

function useCloseNavbarOnChatBotOpen() {
  const setShowSideNav = useSetAtom(isNavigationOpenAtom);
  const showChatBot = useAtomValue(ShowChatBotAtom);
  useLayoutEffect(() => {
    if (showChatBot && window.innerWidth < Breakpoint.xLarge) {
      setShowSideNav(false);
    }
  }, [showChatBot, setShowSideNav]);
}

export function ChatBotSection() {
  const panelRef = usePanelRef();
  useCloseNavbarOnChatBotOpen();
  const onResize = useChatBotPanelStateSync(panelRef);

  return (
    <>
      <Separator />
      <Panel
        // Type assertion needed due to library typing mismatch between usePanelRef return type and panelRef prop type
        panelRef={panelRef as Ref<PanelImperativeHandle>}
        id='chatbot-drawer'
        collapsible
        collapsedSize={0}
        defaultSize={0}
        onResize={onResize}
        minSize={350}
      >
        <ChatBotDrawer />
      </Panel>
    </>
  );
}
